import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { Me, getUser } from "../../api/users";
import { PostGet } from "../../api/post";
import { getServiceNote, approveServiceNote } from "../../api/service_note";
import { TYLevel } from "../../constants";

const STATUS_TEXT = {
  1: "Не согласовано",
  2: "На доработке",
  3: "Согласовано",
  4: "Отклонено",
};

function errorText(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  if (typeof payload.detail === "string") return payload.detail;
  if (Array.isArray(payload.detail) && payload.detail[0]?.msg) {
    return payload.detail[0].msg;
  }
  return fallback;
}

function statusTone(statusId, color) {
  if (color === "green" || Number(statusId) === 3) return "ok";
  if (color === "yellow" || Number(statusId) === 2) return "rework";
  if (Number(statusId) === 4) return "reject";
  return "wait";
}

function formatDateTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDay(value) {
  if (!value) return "—";
  const iso = String(value).slice(0, 10);
  const [, month, day] = iso.split("-");
  return day && month ? `${day}.${month}` : iso;
}

function formatTemplateShiftInfo(templateShift) {
  if (!templateShift) return "—";
  const parts = [];
  if (templateShift.date) parts.push(formatDay(templateShift.date));
  if (templateShift.station_name) parts.push(templateShift.station_name);
  if (templateShift.start != null && templateShift.end != null) {
    parts.push(`${templateShift.start}:00 — ${templateShift.end}:00`);
  }
  if (templateShift.template_shift_id) {
    parts.push(`шаблон № ${templateShift.template_shift_id}`);
  }
  return parts.join(" · ") || "—";
}

function formatEmployeeName(employeeId, snapshotName, employeeNames) {
  if (snapshotName) return snapshotName;
  if (employeeId && employeeNames[employeeId]) return employeeNames[employeeId];
  if (employeeId) return `ID ${employeeId}`;
  return "—";
}

function SummaryField({ label, value }) {
  return (
    <div className="sn-field">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function OperationSummary({ children }) {
  return <div className="sn-op-grid">{children}</div>;
}

function collectEmployeeIds(note) {
  const payload = note?.payload || {};
  const snapshot = payload.snapshot || {};
  const ids = new Set();
  const names = {};

  function addEmployee(employeeId, employeeName) {
    if (!employeeId) return;
    if (employeeName) {
      names[employeeId] = employeeName;
    } else {
      ids.add(employeeId);
    }
  }

  function addShiftPart(part) {
    if (!part) return;
    addEmployee(part.employee_id, part.employee_name);
  }

  switch (note?.operation_type) {
    case "create_shift":
      addEmployee(
        payload.employee_id || snapshot.employee_id,
        snapshot.employee_name,
      );
      break;
    case "delete_shift":
      addShiftPart(snapshot);
      break;
    case "replace_employee":
      addShiftPart(snapshot.shift);
      addEmployee(payload.new_employee_id, snapshot.new_employee_name);
      break;
    case "swap_employees":
      addShiftPart(snapshot.shift_a);
      addShiftPart(snapshot.shift_b);
      break;
    default:
      break;
  }

  return { ids: [...ids], names };
}

function describeOperationEffect(note, employeeNames, tense = "future") {
  const payload = note.payload || {};
  const snapshot = payload.snapshot || {};
  const future = tense === "future";

  switch (note.operation_type) {
    case "create_shift": {
      const employee = formatEmployeeName(
        payload.employee_id || snapshot.employee_id,
        snapshot.employee_name,
        employeeNames,
      );
      const shift = formatTemplateShiftInfo(snapshot.template_shift);
      return future
        ? `В расписание будет добавлена смена: ${employee}, ${shift}.`
        : `В расписание добавлена смена: ${employee}, ${shift}.`;
    }
    case "delete_shift": {
      const employee = formatEmployeeName(
        snapshot.employee_id,
        snapshot.employee_name,
        employeeNames,
      );
      const shift = formatTemplateShiftInfo(snapshot);
      return future
        ? `Из расписания будет удалена смена: ${employee}, ${shift}.`
        : `Из расписания удалена смена: ${employee}, ${shift}.`;
    }
    case "replace_employee": {
      const current = formatEmployeeName(
        snapshot.shift?.employee_id,
        snapshot.shift?.employee_name,
        employeeNames,
      );
      const next = formatEmployeeName(
        payload.new_employee_id,
        snapshot.new_employee_name,
        employeeNames,
      );
      const shift = formatTemplateShiftInfo(snapshot.shift);
      return future
        ? `На смене ${shift} сотрудник ${current} будет заменён на ${next}.`
        : `На смене ${shift} сотрудник ${current} заменён на ${next}.`;
    }
    case "swap_employees": {
      const employeeA = formatEmployeeName(
        snapshot.shift_a?.employee_id,
        snapshot.shift_a?.employee_name,
        employeeNames,
      );
      const employeeB = formatEmployeeName(
        snapshot.shift_b?.employee_id,
        snapshot.shift_b?.employee_name,
        employeeNames,
      );
      const shiftA = formatTemplateShiftInfo(snapshot.shift_a);
      const shiftB = formatTemplateShiftInfo(snapshot.shift_b);
      const details = `${employeeA} (${shiftA}) ↔ ${employeeB} (${shiftB})`;
      return future
        ? `Сотрудники поменяются сменами: ${details}.`
        : `Сотрудники поменялись сменами: ${details}.`;
    }
    default:
      return null;
  }
}

function describeApprovalOutcome(note, employeeNames) {
  const effectFuture = describeOperationEffect(note, employeeNames, "future");
  const effectPast = describeOperationEffect(note, employeeNames, "past");
  if (!effectFuture) {
    return {
      title: "Следствие согласования",
      text: "Для этого типа операции описание недоступно.",
      tone: "reject",
    };
  }

  if (note.applied_at) {
    return {
      title: "Изменение применено",
      text: effectPast,
      tone: "ok",
    };
  }

  if (note.apply_error) {
    return {
      title: "Не удалось применить",
      text: `${effectFuture} При попытке согласования возникла ошибка: ${note.apply_error}`,
      tone: "wait",
    };
  }

  if (note.status_id === 4) {
    return {
      title: "Записка отклонена",
      text: "Изменения в расписание не вносятся.",
      tone: "reject",
    };
  }

  if (note.status_id === 2) {
    return {
      title: "На доработке",
      text: `${effectFuture} Изменения в расписание не вносятся, пока ТУ не согласует записку повторно.`,
      tone: "rework",
    };
  }

  return {
    title: "При согласовании ТУ",
    text: effectFuture,
    tone: "wait",
  };
}

function renderOperationSummary(note, employeeNames) {
  const payload = note.payload || {};
  const snapshot = payload.snapshot || {};

  switch (note.operation_type) {
    case "create_shift":
      return (
        <OperationSummary>
          <SummaryField
            label="Сотрудник"
            value={formatEmployeeName(
              payload.employee_id || snapshot.employee_id,
              snapshot.employee_name,
              employeeNames,
            )}
          />
          <SummaryField
            label="Шаблон смены"
            value={formatTemplateShiftInfo(snapshot.template_shift)}
          />
        </OperationSummary>
      );
    case "delete_shift":
      return (
        <OperationSummary>
          <SummaryField
            label="Сотрудник"
            value={formatEmployeeName(
              snapshot.employee_id,
              snapshot.employee_name,
              employeeNames,
            )}
          />
          <SummaryField
            label="Шаблон смены"
            value={formatTemplateShiftInfo(snapshot)}
          />
        </OperationSummary>
      );
    case "replace_employee":
      return (
        <OperationSummary>
          <SummaryField
            label="Текущий сотрудник"
            value={formatEmployeeName(
              snapshot.shift?.employee_id,
              snapshot.shift?.employee_name,
              employeeNames,
            )}
          />
          <SummaryField
            label="Новый сотрудник"
            value={formatEmployeeName(
              payload.new_employee_id,
              snapshot.new_employee_name,
              employeeNames,
            )}
          />
          <SummaryField
            label="Шаблон смены"
            value={formatTemplateShiftInfo(snapshot.shift)}
          />
        </OperationSummary>
      );
    case "swap_employees":
      return (
        <>
          <OperationSummary>
            <SummaryField
              label="Смена 1 — сотрудник"
              value={formatEmployeeName(
                snapshot.shift_a?.employee_id,
                snapshot.shift_a?.employee_name,
                employeeNames,
              )}
            />
            <SummaryField
              label="Смена 1 — шаблон"
              value={formatTemplateShiftInfo(snapshot.shift_a)}
            />
          </OperationSummary>
          <OperationSummary>
            <SummaryField
              label="Смена 2 — сотрудник"
              value={formatEmployeeName(
                snapshot.shift_b?.employee_id,
                snapshot.shift_b?.employee_name,
                employeeNames,
              )}
            />
            <SummaryField
              label="Смена 2 — шаблон"
              value={formatTemplateShiftInfo(snapshot.shift_b)}
            />
          </OperationSummary>
        </>
      );
    default:
      return null;
  }
}

const ServiceNoteDetail = ({ token }) => {
  const { id } = useParams();
  const [note, setNote] = useState(null);
  const [accessLevel, setAccessLevel] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState(null);
  const [tyApprove, setTyApprove] = useState({ status_id: "1", TY_comment: "" });
  const [saving, setSaving] = useState(false);
  const [employeeNames, setEmployeeNames] = useState({});

  const isTy = accessLevel >= TYLevel;
  const canApprove = isTy && note && !note.applied_at;
  const hasOperationSummary = [
    "create_shift",
    "delete_shift",
    "replace_employee",
    "swap_employees",
  ].includes(note?.operation_type);
  const approvalOutcome =
    note && hasOperationSummary
      ? describeApprovalOutcome(note, employeeNames)
      : null;
  const tyTone = note ? statusTone(note.status_id, note.status_color) : "wait";

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      try {
        const user = await Me(token);
        const post = await PostGet(token, user.post_id);
        setAccessLevel(post.access_level);

        const data = await getServiceNote(token, id);
        if (data?.detail && !Number.isFinite(data?.id)) {
          setError(errorText(data, "Не удалось загрузить служебную записку"));
          return;
        }
        setNote(data);
        setTyApprove({
          status_id: String(data.status_id ?? 1),
          TY_comment: data.TY_comment || "",
        });
      } catch (err) {
        console.error(err);
        setError("Не удалось загрузить служебную записку");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token, id]);

  useEffect(() => {
    if (!note || !token || !hasOperationSummary) {
      setEmployeeNames({});
      return;
    }

    const { ids, names } = collectEmployeeIds(note);
    if (ids.length === 0) {
      setEmployeeNames(names);
      return;
    }

    let cancelled = false;
    async function loadEmployees() {
      const fetched = { ...names };
      await Promise.all(
        ids.map(async (employeeId) => {
          const user = await getUser(token, employeeId);
          if (user?.detail) return;
          fetched[employeeId] = `${user.surname || ""} ${user.name || ""}`.trim();
        }),
      );
      if (!cancelled) {
        setEmployeeNames(fetched);
      }
    }
    loadEmployees();
    return () => {
      cancelled = true;
    };
  }, [note, token, hasOperationSummary]);

  async function handleApprove() {
    setMessage(null);
    setSaving(true);
    try {
      const data = await approveServiceNote(token, id, {
        status_id: Number(tyApprove.status_id),
        TY_comment: tyApprove.TY_comment,
      });
      if (data?.detail && !Number.isFinite(data?.id)) {
        setMessage({
          ok: false,
          text: errorText(data, "Ошибка сохранения"),
        });
        return;
      }
      setNote(data);
      setTyApprove({
        status_id: String(data.status_id ?? 1),
        TY_comment: data.TY_comment || "",
      });
      if (data.applied_at) {
        setMessage({
          ok: true,
          text: "Согласовано и применено к расписанию",
        });
      } else if (Number(tyApprove.status_id) === 3) {
        setMessage({
          ok: false,
          text: errorText(data, "Не удалось применить изменение"),
        });
      } else {
        setMessage({ ok: true, text: "Сохранено" });
      }
    } catch (err) {
      console.error(err);
      setMessage({ ok: false, text: "Ошибка сохранения" });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="sn-page">
        <div className="reports-loading">Загрузка служебной записки…</div>
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="sn-page">
        <div className="login-error">{error || "Записка не найдена"}</div>
        <Link to="/report-service-notes" className="reports-back-link">
          ← К списку
        </Link>
      </div>
    );
  }

  return (
    <div className="sn-page">
      <div className="reports-header">
        <div>
          <h1>Служебная записка № {note.id}</h1>
          <p className="reports-hint">
            {note.restaurant_name} · {formatDay(note.start_date)} —{" "}
            {formatDay(note.end_date)} ·{" "}
            {note.operation_type_label || note.operation_type}
          </p>
        </div>
        <div className="sn-header-actions">
          <Link to="/report-service-notes" className="reports-back-link">
            ← К списку
          </Link>
          <Link to="/reports" className="reports-back-link">
            К отчётам
          </Link>
        </div>
      </div>

      <div className="chk-summary-kpis sn-kpis sn-kpis--detail">
        <div className="chk-summary-kpi">
          <span>Создана</span>
          <strong className="sn-kpi-text">{formatDateTime(note.created)}</strong>
        </div>
        <div className="chk-summary-kpi">
          <span>Директор</span>
          <strong className="sn-kpi-text">{note.creater_name}</strong>
        </div>
        <div className="chk-summary-kpi">
          <span>Применено</span>
          <strong className="sn-kpi-text">
            {note.applied_at
              ? formatDateTime(note.applied_at)
              : note.apply_error
                ? "Ошибка"
                : "Нет"}
          </strong>
        </div>
      </div>

      {approvalOutcome ? (
        <div className={`chk-approve-summary is-${approvalOutcome.tone}`}>
          <div>
            <h4>{approvalOutcome.title}</h4>
            <p>{approvalOutcome.text}</p>
          </div>
          <span className={`chk-approve-badge is-${tyTone}`}>
            {note.status_name || STATUS_TEXT[note.status_id] || "—"}
          </span>
        </div>
      ) : null}

      <div className="chk-approve">
        <div className="chk-approve-pipeline">
          <article className="chk-approve-card">
            <header>
              <span>Директор</span>
            </header>
            <strong className="chk-approve-badge is-ok">Автор записки</strong>
            <p className="chk-approve-name">{note.creater_name}</p>
            <p className="chk-approve-comment">{note.comment || "Нет комментария"}</p>
          </article>
          <div className="chk-approve-arrow" aria-hidden="true" />
          <article
            className={`chk-approve-card is-${tyTone}${isTy ? " is-mine" : ""}`}
          >
            <header>
              <span>ТУ</span>
              {isTy ? <em>вы</em> : null}
            </header>
            <strong className={`chk-approve-badge is-${tyTone}`}>
              {note.status_name || STATUS_TEXT[note.status_id] || "—"}
            </strong>
            <p className="chk-approve-name">
              {note.TY_surname_name || "Ещё не указан"}
            </p>
            <p className="chk-approve-comment">
              {note.TY_comment || "Нет комментария"}
            </p>
          </article>
        </div>

        {canApprove ? (
          <div className="chk-approve-form">
            <h5>Ваше согласование · ТУ</h5>
            <div className="sn-approve-grid">
              <label className="users-field">
                <span>Этап согласования</span>
                <select
                  className="form-select"
                  value={tyApprove.status_id}
                  onChange={(event) =>
                    setTyApprove({ ...tyApprove, status_id: event.target.value })
                  }
                >
                  <option value="1">Не согласовано</option>
                  <option value="2">На доработке</option>
                  <option value="3">Согласовано</option>
                  <option value="4">Отклонено</option>
                </select>
              </label>
              <label className="users-field">
                <span>Комментарий ТУ</span>
                <textarea
                  className="form-control"
                  rows={3}
                  value={tyApprove.TY_comment}
                  onChange={(event) =>
                    setTyApprove({ ...tyApprove, TY_comment: event.target.value })
                  }
                />
              </label>
            </div>
            <div className="chk-approve-form-footer">
              {message ? (
                <span className={message.ok ? "is-ok" : "is-error"}>
                  {message.text}
                </span>
              ) : (
                <span />
              )}
              <button
                type="button"
                className="btn"
                onClick={handleApprove}
                disabled={saving}
              >
                {saving ? "Сохранение…" : "Сохранить согласование"}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <section className="sn-operation">
        <div className="chk-more-card-head">
          <h4>Операция</h4>
          <p>{note.operation_type_label || note.operation_type}</p>
        </div>
        {hasOperationSummary ? renderOperationSummary(note, employeeNames) : null}
        {note.apply_error ? (
          <p className="sn-apply-error">
            <strong>Ошибка применения:</strong> {note.apply_error}
          </p>
        ) : null}
        {isTy && note.operation?.result ? (
          <details className="sn-json">
            <summary>Результат применения</summary>
            <pre>{JSON.stringify(note.operation.result, null, 2)}</pre>
          </details>
        ) : null}
      </section>
    </div>
  );
};

export default ServiceNoteDetail;
